import { useEffect, useRef } from "react";

/**
 * Calls `callback` again and again, `intervalMs` after the previous call has finished.
 *
 * - Waits for the previous call to finish, so slow responses never overlap.
 * - Skips calls while the browser tab is hidden.
 * - Stops when `enabled` is false and when the component unmounts.
 */
export function usePolling(callback, intervalMs, enabled = true) {
  const savedCallback = useRef(callback);

  // Always call the latest callback, without restarting the timer on every render
  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (!enabled || !intervalMs) return;

    let timer = null;
    let cancelled = false;

    const tick = async () => {
      if (!document.hidden) {
        try {
          await savedCallback.current();
        } catch (error) {
          // The callback handles its own errors; never let one stop the polling
        }
      }
      if (!cancelled) timer = setTimeout(tick, intervalMs);
    };

    timer = setTimeout(tick, intervalMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [intervalMs, enabled]);
}
